import {createApi, fetchBaseQuery} from "@reduxjs/toolkit/query/react"
import {BROADCAST_API_TOKEN, BROADCAST_API_URL, IS_BROADCAST_API_CONFIGURED} from "../../utils/api"
import {getAdminLanguage} from "../../utils/language"
import {
    IBroadcastPayload,
    IBroadcastResponse,
    IBroadcastsListResponse,
    IUpdateBroadcastParams
} from "./BroadcastApiTypes"

const rawBroadcastBaseQuery = fetchBaseQuery({
    baseUrl: BROADCAST_API_URL,
    prepareHeaders: (headers) => {
        headers.set("Content-Type", "application/json")
        if (BROADCAST_API_TOKEN) {
            headers.set("Authorization", `Bearer ${BROADCAST_API_TOKEN}`)
        }
        headers.set("Accept-Language", getAdminLanguage())

        return headers
    }
})

const broadcastBaseQuery: typeof rawBroadcastBaseQuery = async (args, api, extraOptions) => {
    if (!IS_BROADCAST_API_CONFIGURED) {
        return {
            error: {
                status: "CUSTOM_ERROR",
                error: "Broadcast API is not configured"
            }
        }
    }

    return rawBroadcastBaseQuery(args, api, extraOptions)
}

export const broadcastApi = createApi({
    reducerPath: "broadcastApi",
    baseQuery: broadcastBaseQuery,
    tagTypes: ["broadcasts"],
    endpoints: (build) => ({
        getBroadcasts: build.query<IBroadcastsListResponse, void>({
            query: () => ({
                url: "/api/broadcasts",
                method: "GET"
            }),
            providesTags: (result) => [
                "broadcasts",
                ...(result?.broadcasts || []).map((broadcast) => ({
                    type: "broadcasts" as const,
                    id: broadcast.id
                }))
            ]
        }),
        getBroadcastById: build.query<IBroadcastResponse, string>({
            query: (id) => ({
                url: `/api/broadcasts/${id}`,
                method: "GET"
            }),
            providesTags: (_result, _error, id) => [{type: "broadcasts", id}]
        }),
        createBroadcast: build.mutation<IBroadcastResponse, IBroadcastPayload>({
            query: (body) => ({
                url: "/api/broadcasts",
                method: "POST",
                body
            }),
            invalidatesTags: ["broadcasts"]
        }),
        updateBroadcast: build.mutation<IBroadcastResponse, IUpdateBroadcastParams>({
            query: ({id, ...body}) => ({
                url: `/api/broadcasts/${id}`,
                method: "PATCH",
                body
            }),
            invalidatesTags: (_result, _error, {id}) => ["broadcasts", {type: "broadcasts", id}]
        }),
        startBroadcast: build.mutation<IBroadcastResponse, string>({
            query: (id) => ({
                url: `/api/broadcasts/${id}/start`,
                method: "POST"
            }),
            invalidatesTags: (_result, _error, id) => ["broadcasts", {type: "broadcasts", id}]
        }),
        stopBroadcast: build.mutation<IBroadcastResponse, string>({
            query: (id) => ({
                url: `/api/broadcasts/${id}/stop`,
                method: "POST"
            }),
            invalidatesTags: (_result, _error, id) => ["broadcasts", {type: "broadcasts", id}]
        })
    })
})

export const {
    useGetBroadcastsQuery,
    useGetBroadcastByIdQuery,
    useCreateBroadcastMutation,
    useUpdateBroadcastMutation,
    useStartBroadcastMutation,
    useStopBroadcastMutation
} = broadcastApi
